/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { compose } from 'recompose';
import { withRouter } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import ReactMarkdown from 'react-markdown';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import EditIcon from '@material-ui/icons/Edit';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { useUIDSeed } from 'react-uid';
import {
    object, string, number, node, shape, array,
} from 'prop-types';

import { RenderChatAsLink } from '../../MultiBrandChatController/AigoChatWidgetWrapperV2/popOpenChat';
import CustomerOptionsBlock from './Partials/CustomerOptionsBlock';
import GraphqlBreadcrumbs from '../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import HeroBlock from './Partials/HeroBlock';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import CustomerServiceContainer from './ContactUsBanner/ContactUsBannerContainer';
import { getFeatureFlags, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import OutageMessage from './Partials/OutageMessage/OutageMessage';
import TwilloSearchBar from './Partials/TwilloSearchBar';
import AlertMessage from '../AlertMessage/AlertMessage';
import LinkBlock from './Partials/LinkBlock';
import GuaranteeBlock from './Partials/GuaranteeBlock';
import DesktopMobile from '../../../helpers/DesktopMobile/DesktopMobile';
import { setChatbotOpenState } from '../../../../state/ducks/App/App-Actions';
import toggleWebChat from '../../../helpers/toggleWebChat';
import { nodeToString } from '../../../helpers/common/helper';
import isHtml from '../../../helpers/contentstack/isHtml';

const styles = (theme) => ({
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
    },
    faqContainer: {
        maxWidth: '80%',
        margin: 'auto',
        width: '100%',
        [theme.breakpoints.down('xs')]: {
            maxWidth: '95%',
        },
        [theme.breakpoints.down(1025)]: {
            '& .MuiTabs-indicator': {
                display: 'none',
            },
            '& .MuiTab-root.MuiTab-textColorInherit.Mui-selected': {
                borderBottom: `2px solid ${theme.palette.secondary.main}`,
            },
        },
        [theme.breakpoints.down(601)]: {
            '& .MuiTabs-indicator': {
                display: 'inline-block',
            },
            '& .MuiTab-root.MuiTab-textColorInherit.Mui-selected': {
                borderBottom: 'none',
            },
        },
        '& h3': {
            padding: '10px',
            fontSize: '14px',
            fontWeight: 'normal',
            marginBottom: 0,
            color: theme.palette.cms?.customerFaqHeading || '#000000',
        },
        '& button': {
            maxWidth: 350,
            textTransform: 'capitalize',
            fontWeight: 'bold',
            [theme.breakpoints.down('xs')]: {
                fontSize: 12,
                maxWidth: 120,
            },
        },
        '& .MuiTab-textColorInherit.Mui-selected': {
            background: theme.palette.cms?.white || '#ffffff',
            border: `1px solid ${theme.palette.cms?.customerTabPanelHeading || 'transparent'}`,
            color: theme.palette.cms?.customerFaqHeading || ' #000000',
        },
        '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.cms?.customerTabIndicator || theme.palette.cms?.colorSecondary,
            borderTop: `1px solid ${theme.palette.cms?.customerIndicatorBorder || 'transparent'}`,
        },
    },
    justifyContent: {
        justifyContent: 'space-between',
        backgroundColor: theme.palette.cms?.customerTabPanelBg || '#ffffff',
        color: theme.palette.cms?.customerTabPanelHeading || '#000000',
        borderBottom: `1px solid ${theme.palette.cms?.customerTabPanelHeading || 'transparent'}`,
        '& .MuiTab-wrapper': {
            flexDirection: 'row',
        },
        [theme.breakpoints.down(601)]: {
            display: 'flex',
            whiteSpace: 'nowrap',
        },
    },
    answers: {
        padding: '30px',
        margin: '0',
        background: theme.palette.cms?.csBgColor || theme.palette.csBgColor,
        color: theme.palette.cms?.customerFaqHeading || '#000000',
        '& a': {
            color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        },
    },
    chatBotButton: {
        color: theme.palette.cms?.customerChatLink || '#223970',
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    tabPanel: {
        maxHeight: '300px',
        overflow: 'auto',
        border: `1px solid ${theme.palette.cms?.customerTabPanelHeading || 'transparent'}`,
        borderTop: 'none',
    },
    bannerContainer: {
        position: 'relative',
        '& img': {
            width: '100%',
            display: 'block',
            marginTop: '16px',
        },
    },
    // place holder for banner copy styles
    bannerCopy: {
    },
});

function TabPanel(props) {
    const {
        children, value, index, ...other
    } = props;

    return (
        <>
            <div
                component="div"
                role="tabpanel"
                hidden={value !== index}
                id={`wrapped-tabpanel-${index + 1}`}
                aria-labelledby={`wrapped-tab-${index + 1}`}
                {...other}
            >
                {value === index ? (
                    <>
                        {children}
                    </>
                ) : null}
            </div>
        </>
    );
}

TabPanel.propTypes = {
    children: node.isRequired,
    index: number.isRequired,
    value: number.isRequired,
};

const CustomerServiceBuilder = ({
    classes, data, brand, page, featureFlags, history, presentationFamily,
}) => {
    const seed = useUIDSeed();
    let heroData = {};
    let optionsData = {};
    let faq = {};
    let banner = {};
    let guarantee = {};
    let linkBlock = {};
    let seo;
    if (data.seo) {
        seo = JSON.parse(JSON.stringify(data.seo));
    } else {
        seo = {};
    }

    if (data?.modular_blocks?.length && data !== undefined) {
        data.modular_blocks.forEach((dataList) => {
            const type = Object.keys(dataList)[0];
            switch (type) {
                case 'hero':
                    heroData = dataList?.hero;
                    break;
                case 'options':
                    optionsData = dataList;
                    break;
                case 'faq':
                    faq = dataList?.faq;
                    break;
                case 'guarantee':
                    guarantee = dataList?.guarantee;
                    break;
                case 'link_block':
                    linkBlock = dataList?.link_block;
                    break;
                case 'banner':
                    banner = dataList?.banner;
                    break;
                default:
                    return null;
            }
            return null;
        });
    }
    const [value, setValue] = React.useState(0);
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    useEffect(() => {
        let tabNameData = [];
        if (presentationFamily === 'flower') {
            if (data?.modular_blocks?.length && data !== undefined) {
                data.modular_blocks.forEach((dataList) => {
                    if (dataList?.faq) {
                        tabNameData = dataList.faq?.group;
                    }
                });
            }

            const tabName = tabNameData.map((tabData) => (
                tabData?.faq_topic?.replace(/\s+/g, '').toLowerCase()
            ));

            const tabValueUrl = history?.location?.search.match('(active-tab)=([^&]+)');

            tabName.forEach((tabNameValue, index) => {
                if (tabNameValue === tabValueUrl?.[2].toLowerCase()) {
                    setValue(index);
                }
            });
        }

        // set tabindex on scrollable div for accessibility
        if (typeof document !== 'undefined' && typeof window !== 'undefined') {
            const setTabindex = document.getElementsByClassName('MuiTabs-scrollable');
            if (setTabindex.length > 0) {
                setTabindex?.[0]?.setAttribute('tabindex', '1'); // added the tabindex attibute when scrollable div exsist in DOM
            }
        }
    }, []);

    const a11yProps = (index) => ({
        id: `wrapped-tab-${index + 1}`,
        'aria-controls': `wrapped-tabpanel-${index + 1}`,
        tabIndex: index + 1,
    });

    const GetIcon = ({ icon }) => {
        switch (icon) {
            case 'Edit':
                return <EditIcon />;
            case 'LocalShippingIcon':
                return <LocalShippingIcon />;
            case 'ErrorOutlineIcon':
                return <ErrorOutlineIcon />;
            case 'MailOutlineIcon':
                return <MailOutlineIcon />;
            default:
                return null;
        }
    };

    GetIcon.propTypes = {
        icon: string.isRequired,
    };
    seo.og = [];
    seo.og.push(
        { property: 'og:title', content: seo.page_title || '' },
        { property: 'og:type', content: 'website' },
        { property: 'og:image', content: 'https://www.1800flower-usa.com/api/fl-cdn/wcsstore/RAPIDStorefrontAssetStore/images/flowers-com.jpg' },
        { property: 'og:description', content: seo.meta_description || '' },
        { property: 'og:url', content: `https://www.${brand.domain}.com${page.path || ''}` },
    );
    const dispatch = useDispatch();
    const renderers = {
        linkReference: (props) => {
            const { href, children } = props;
            if (href === 'mailto:info@1800flower-usa.com' || href === 'info@1800flower-usa.com') {
                return (
                    <span
                        onKeyDown={() => { }}
                        role="button"
                        tabIndex={-1}
                        onClick={() => { dispatch(setChatbotOpenState(true)); toggleWebChat(); }}
                        className={classes.chatBotButton}
                    >{children}
                    </span>
                );
            }
            return <a href={href}>{children}</a>;
        },
        text: (textProps) => {
            const child = textProps.value;
            if (typeof child === 'string' && child?.toLowerCase()?.includes('chat')) {
                return <RenderChatAsLink value={child} />;
            }
            return child;
        },
    };
    const transform = (nod) => {
        if (nod?.type === 'tag' && nod?.name === 'p') {
            const val = nod?.children?.map(nodeToString)?.join('');
            if (val && typeof val === 'string' && val.toLowerCase()?.includes('chat')) {
                return <RenderChatAsLink value={val} />;
            }
        }
        return undefined;
    };

    return (
        <>
            {
                seo?.seo_searchable
                    ? (
                        <GraphqlSeoHeadTags seoData={seo} noscript="customer service" />
                    ) : null
            }

            <div className={classes.container}>
                {seo.breadcrumb && (
                    <div className={classes.breadCrumbs}>
                        <GraphqlBreadcrumbs breadCrumbArray={seo.breadcrumb} />
                    </div>
                )}
                {featureFlags['is-cs-delivery-alert-message-enabled'] && (
                    <AlertMessage queryTitle="Customer Service" />
                )}

                {/* Hero Block */}
                {Object.keys(heroData).length > 0
                    && (
                        <HeroBlock data={heroData} />
                    )}
                {featureFlags['is-atlas-maintenance-alert-enabled'] && (
                    <OutageMessage />
                )}
                {/* Banner */}
                {Object.keys(banner).length > 0
                    && (
                        <div className={classes.bannerContainer} style={{ textAlign: banner.copy_alignment }}>
                            <ReactMarkdown
                                source={banner?.main_copy}
                                skipHtml
                                className={classes.bannerCopy}
                            />
                            <DesktopMobile
                                mobile={() => banner?.mobile_image?.url && <img src={banner.mobile_image.url} alt="Mobile Banner" />}
                                desktop={() => banner?.desktop_image?.url && <img src={banner.desktop_image.url} alt="Desktop Banner" />}

                            />
                        </div>
                    )}

                {/* Customer Search Bar */}
                {featureFlags['is-cs-search-bar-enabled'] && (
                    <TwilloSearchBar />
                )}
                {Object.keys(linkBlock).length > 0
                    && (
                        <LinkBlock data={linkBlock} />
                    )}
                {/* Customer Option Block */}
                {Object.keys(optionsData).length > 0
                    && (
                        <CustomerOptionsBlock data={optionsData} />
                    )}
                {/* Guarantee Block */}
                {Object.keys(guarantee).length > 0
                    && (
                        <GuaranteeBlock data={guarantee} />
                    )}
                {/* Faq Block */}
                {Object.keys(faq).length > 0
                    && (
                        <div id="faqContainer" data-testid="faqContainer" className={classes.faqContainer}>
                            <h3 style={{
                                backgroundColor: faq?.header_background_color,
                                fontSize: `${faq?.header_font_size}em`,
                            }}
                            >
                                {faq?.header}
                            </h3>
                            <Tabs scrollButtons="false" variant="scrollable" value={value} onChange={handleChange} classes={{ flexContainer: classes.justifyContent }} aria-label="wrapped label tabs example">
                                {faq.group.map((d, i) => <Tab key={seed(d)} value={i} label={d?.faq_topic} {...a11yProps(i)} />)}
                            </Tabs>
                            {faq.group.map((d, i) => (
                                <TabPanel className={classes.tabPanel} key={seed(d)} value={value} index={i}>
                                    <div style={{ fontSize: '14px' }}>
                                        {d.group.map((info) => {
                                            if (isHtml(info?.answers)) {
                                                return <div key={seed(info)} className={classes.answers}>{ReactHtmlParser(info?.answers, { transform })}</div>;
                                            }
                                            return (
                                                <ReactMarkdown
                                                    key={seed(info)}
                                                    source={info?.answers}
                                                    escapeHtml={false}
                                                    className={classes.answers}
                                                    renderers={renderers}
                                                />
                                            );
                                        })}

                                    </div>
                                </TabPanel>
                            ))}
                        </div>
                    )}
            </div>
            {featureFlags['is-showing-contact-us-phone-number'] && (
                <CustomerServiceContainer brand={brand} page={page} />
            )}
        </>
    );
};

CustomerServiceBuilder.propTypes = {
    classes: object.isRequired,
    data: shape({
        modular_block: array,
    }).isRequired,
    brand: object.isRequired,
    page: object.isRequired,
    featureFlags: object.isRequired,
    history: shape({
        location: shape({
            search: string.isRequired,
        }).isRequired,
    }).isRequired,
    presentationFamily: string.isRequired,
    href: string,
    children: string,
};
CustomerServiceBuilder.defaultProps = {
    href: '',
    children: '',
};

const mapStateToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
    presentationFamily: getPresentationFamily(state),
});

const enhance = compose(
    withRouter,
    withStyles(styles),
    connect(mapStateToProps, null),
);

export default enhance(CustomerServiceBuilder);
